import React, { useState } from "react";
import { Star, ChevronLeft, ChevronRight, ExternalLink, PencilLine } from "lucide-react";
import { siteData } from "../mock";

// Official Google "G" logo
const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function Reviews() {
  const [idx, setIdx] = useState(0);
  const reviews = siteData.reviews;
  const rating = 5.0;
  const count = siteData.practitioner.reviewsCount;
  const { profileUrl, writeReviewUrl, seeAllReviewsUrl } = siteData.google;

  const prev = () => setIdx((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setIdx((i) => (i + 1) % reviews.length);

  return (
    <section className="container-tight py-24" data-testid="reviews-section">

      <div className="flex justify-between items-end mb-12 flex-wrap gap-4">
        <div className="max-w-2xl">
          <span className="chip mb-4">Avis Google vérifiés</span>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-[#1a1815] tracking-tight leading-[1.05]">
            Ce que disent celles et ceux que j'accompagne
          </h2>
        </div>

        {/* Google rating card */}
        <a
          href={seeAllReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-rating-card"
          className="group flex items-center gap-4 bg-white rounded-2xl border border-[#e8e0d5] px-5 py-3 hover:border-[#1a1815] hover:shadow-md transition-all"
        >
          <GoogleG size={26} />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-[#1a1815] font-semibold text-lg leading-none">{rating.toFixed(1)}</span>
              <div className="flex items-center gap-0.5 text-[#f5a623]">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill="currentColor" strokeWidth={0} />)}
              </div>
            </div>
            <span className="text-xs text-[#4a4642] mt-0.5">
              {count} avis Google · <span className="underline group-hover:text-[#1a1815]">voir sur Google</span>
            </span>
          </div>
        </a>
      </div>

      {/* Review cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {[0, 1, 2].map((offset) => {
          const r = reviews[(idx + offset) % reviews.length];
          return (
            <div
              key={offset}
              data-testid={`review-card-${offset}`}
              className="bg-white rounded-3xl p-7 border border-[#e8e0d5] hover:shadow-lg transition-all relative"
            >
              <div className="absolute top-5 right-5 opacity-80" title="Avis Google">
                <GoogleG size={18} />
              </div>
              <div className="flex gap-1 text-[#f5a623] mb-4">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} fill="currentColor" strokeWidth={0} />)}
              </div>
              <p className="text-[#4a4642] leading-relaxed text-[15px] mb-6">
                &laquo; {r.text} &raquo;
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-[#e8e0d5]">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f0e4d5] to-[#d97757]/40 flex items-center justify-center font-semibold text-[#1a1815]">
                  {r.name.charAt(0)}
                </div>
                <div>
                  <div className="font-medium text-sm text-[#1a1815]">{r.name}</div>
                  <div className="text-xs text-[#8a8580]">{r.city}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Nav + CTAs */}
      <div className="flex flex-wrap justify-between items-center gap-6 mt-10">
        <div className="flex items-center gap-3">
          <button
            onClick={prev}
            data-testid="reviews-prev-btn"
            aria-label="Avis précédents"
            className="w-11 h-11 rounded-full border border-[#e8e0d5] hover:border-[#1a1815] flex items-center justify-center transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            data-testid="reviews-next-btn"
            aria-label="Avis suivants"
            className="w-11 h-11 rounded-full border border-[#e8e0d5] hover:border-[#1a1815] flex items-center justify-center transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <a
            href={seeAllReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-all-google-reviews-btn"
            className="inline-flex items-center gap-2 bg-white border border-[#e8e0d5] hover:border-[#1a1815] text-[#1a1815] px-5 py-3 rounded-full text-sm font-medium transition-colors"
          >
            <GoogleG size={16} />
            Voir tous mes avis
            <ExternalLink size={14} />
          </a>
          <a
            href={writeReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-google-review-btn"
            className="inline-flex items-center gap-2 bg-[#1a1815] hover:bg-[#d97757] text-white px-5 py-3 rounded-full text-sm font-medium transition-colors"
          >
            <PencilLine size={16} />
            Laisser un avis Google
          </a>
        </div>
      </div>
    </section>
  );
}
